import { type RecentDraftInsertion, type User } from '@/types';

import { ApiResponseError } from '@/api/ApiResponseError';
import { createRequest, get } from '@/api/fetch';
import { userNotFound } from '@/helpers/metricUtils';
import { captureError } from '@smg-real-estate/browser-rum';
import { isEmpty } from 'lodash-es';

const getUserRequest = createRequest({ method: 'GET', expectedStatuses: [404] });

export interface BillingContact {
  id?: string;
  personId: string;
  platform: string;
  name?: string;
  email?: string;
  salutation?: string;
  language?: string;
  phone?: string;
  companyName?: string;
  street?: string;
  postalCode?: string;
  locality?: string;
  region?: string;
  country?: string;
  billToContactId?: string;
  createdAt?: string;
}

export async function getUser(userId: string): Promise<User | null> {
  try {
    const userUrl = `${import.meta.env.VUE_APP_INSERTION_FUNNEL_API_BASE_URL}/users/${userId}`;
    const response = await getUserRequest(userUrl);
    return response.json();
  } catch (error) {
    if (error instanceof ApiResponseError && error.statusCode === 404) {
      userNotFound();
      return null;
    }
    throw error;
  }
}

export async function getBillingContacts(userId: string): Promise<BillingContact[]> {
  try {
    const url = `${import.meta.env.VUE_APP_INSERTION_FUNNEL_API_BASE_URL}/users/${userId}/billing-contact`;
    const response = await get(url);
    const body = await response.json();
    return Array.isArray(body) ? body : [];
  } catch (error) {
    captureError(error, {
      service: 'users-api',
      action: 'get-billing-contacts',
      userId,
    });
    throw error;
  }
}

export async function getRecentDraftInsertion(
  userId: string,
): Promise<RecentDraftInsertion | null> {
  try {
    const endpoint = `${import.meta.env.VUE_APP_INSERTION_FUNNEL_API_BASE_URL}/users/${userId}/insertions/recent-draft-insertion`;
    const response: Response = await get(endpoint);
    const responseText = await response.text();

    if (!responseText.trim()) return null;

    const responseBody = JSON.parse(responseText);

    if (isEmpty(responseBody)) return null;

    return responseBody as RecentDraftInsertion;
  } catch (error) {
    captureError(error, {
      service: 'users-api',
      action: 'get-recent-draft-insertion',
      userId,
    });
    throw error;
  }
}
